import { useEffect, useState } from "react";
import type { AvatarConfig, HatId } from "@shared/types";
import { api } from "../api";

const SKIN = ["#f3d4b3", "#e6c39a", "#d7a47f", "#c1865d", "#9c6440", "#6e4222"];
const SHIRT = [
  "#22c55e", "#0ea5e9", "#9b5cf6", "#ef476f", "#f97316",
  "#ffd166", "#06d6a0", "#1e293b",
];
const PANTS = [
  "#334155", "#1d4ed8", "#7c3aed", "#16a34a",
  "#dc2626", "#ea580c", "#92400e", "#0f766e",
];
const HATS: { id: HatId; label: string }[] = [
  { id: "none", label: "None" },
  { id: "cap-blue", label: "Blue cap" },
  { id: "cap-red", label: "Red cap" },
  { id: "beanie", label: "Beanie" },
  { id: "crown", label: "Crown" },
];

interface Props {
  initial: AvatarConfig;
  onSaved?: (next: AvatarConfig) => void;
  /** When true, AvatarEditor is purely controlled — no Save button or API call.
   *  Used by the "create player" flow where the parent submits everything at once.
   */
  controlledOnly?: boolean;
  onChange?: (cfg: AvatarConfig) => void;
  localOnly?: boolean;
}

const DEFAULTS: Required<Pick<AvatarConfig, "skinColor" | "shirtColor" | "pantsColor">> = {
  skinColor: "#d7a47f",
  shirtColor: "#22c55e",
  pantsColor: "#334155",
};

export function AvatarEditor({ initial, onSaved, controlledOnly, onChange, localOnly }: Props) {
  const [cfg, setCfg] = useState<AvatarConfig>({ ...DEFAULTS, ...initial });
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setCfg({ ...DEFAULTS, ...initial });
  }, [initial]);

  const set = (k: keyof AvatarConfig, v: string) => {
    setCfg((c) => {
      const next = { ...c, [k]: v };
      onChange?.(next);
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      if (!localOnly) await api.patch("/api/students/me", { avatarConfig: cfg });
      setSavedAt(Date.now());
      onSaved?.(cfg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={controlledOnly ? "stack" : "card stack"}>
      <div className="row" style={{ alignItems: "flex-start", gap: 24, flexWrap: "wrap" }}>
        <AvatarPreview cfg={cfg} />
        <div className="stack" style={{ flex: 1, minWidth: 220 }}>
          <Row label="Skin" value={cfg.skinColor} onPick={(v) => set("skinColor", v)} swatches={SKIN} />
          <Row label="Shirt" value={cfg.shirtColor} onPick={(v) => set("shirtColor", v)} swatches={SHIRT} />
          <Row label="Pants" value={cfg.pantsColor} onPick={(v) => set("pantsColor", v)} swatches={PANTS} />
          <div>
            <div className="muted" style={{ fontSize: "0.85rem", marginBottom: 6 }}>Hat</div>
            <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
              {HATS.map((h) => (
                <button
                  key={h.id}
                  className={cfg.hatId === h.id || (!cfg.hatId && h.id === "none") ? "" : "ghost"}
                  style={{ padding: "8px 12px", fontSize: "0.85rem" }}
                  onClick={() => set("hatId" as keyof AvatarConfig, h.id)}
                >
                  {h.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      {!controlledOnly && (
        <div className="row-between">
          <span className="muted">
            {savedAt ? `Saved ${new Date(savedAt).toLocaleTimeString()}` : ""}
          </span>
          <button className="btn-primary" onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save look"}
          </button>
        </div>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  onPick,
  swatches,
}: {
  label: string;
  value?: string;
  onPick: (v: string) => void;
  swatches: string[];
}) {
  return (
    <div>
      <div className="muted" style={{ fontSize: "0.85rem", marginBottom: 6 }}>
        {label}
      </div>
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        {swatches.map((c) => (
          <button
            key={c}
            onClick={() => onPick(c)}
            aria-label={`${label} ${c}`}
            className={`swatch ${value === c ? "selected" : ""}`}
            style={{ background: c }}
          />
        ))}
      </div>
    </div>
  );
}

/** Stylized blocky avatar preview — matches the in-game voxel runner. */
export function AvatarPreview({ cfg }: { cfg: AvatarConfig }) {
  const { skinColor = DEFAULTS.skinColor, shirtColor = DEFAULTS.shirtColor, pantsColor = DEFAULTS.pantsColor, hatId } = cfg;
  return (
    <div className="avatar-preview">
      {hatId && hatId !== "none" && <HatPreview id={hatId} />}
      <div className="ap-head" style={{ background: skinColor }}>
        <div className="ap-eye" style={{ left: "28%" }} />
        <div className="ap-eye" style={{ right: "28%" }} />
      </div>
      <div className="ap-shoulders">
        <div className="ap-arm" style={{ background: skinColor }} />
        <div className="ap-torso" style={{ background: shirtColor }} />
        <div className="ap-arm" style={{ background: skinColor }} />
      </div>
      <div className="ap-legs">
        <div className="ap-leg" style={{ background: pantsColor }} />
        <div className="ap-leg" style={{ background: pantsColor }} />
      </div>
    </div>
  );
}

function HatPreview({ id }: { id: HatId }) {
  if (id === "cap-blue" || id === "cap-red") {
    const c = id === "cap-blue" ? "#2563eb" : "#dc2626";
    return (
      <div className="ap-hat-cap">
        <div className="ap-cap-crown" style={{ background: c }} />
        <div className="ap-cap-brim" style={{ background: c }} />
      </div>
    );
  }
  if (id === "beanie") {
    return (
      <div className="ap-hat-beanie">
        <div className="ap-beanie-body" />
        <div className="ap-beanie-pom" />
      </div>
    );
  }
  if (id === "crown") {
    return (
      <div className="ap-hat-crown">
        <span>♛</span>
      </div>
    );
  }
  return null;
}
